import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import {
  type AccountingYear,
  type AvailableYear,
  closeAccountingYear,
  listAccountingYears,
  listAvailableAccountingYears,
  reopenAccountingYear,
} from "../services/accountingYear";
import { getPlReport } from "../services/pl";

const STATUS_LABELS: Record<string, string> = { open: "Open", closed: "Closed" };
const STATUS_BADGE_CLASS: Record<string, string> = {
  open: "badge-neutral",
  closed: "badge-ok",
};

function yearKey(year: { start_date: string; end_date: string }): string {
  return `${year.start_date}|${year.end_date}`;
}

function availableLabel(year: AvailableYear): string {
  const tags = [year.is_current ? "current" : null, year.status === "closed" ? "closed" : null]
    .filter((tag) => tag !== null)
    .join(", ");
  const range = `${formatDate(year.start_date)} – ${formatDate(year.end_date)}`;
  return tags ? `${range} (${tags})` : range;
}

export function yearEndView(): Node {
  const loadError = signal("");
  const years = signal<AccountingYear[]>([]);
  const available = signal<AvailableYear[]>([]);
  const selectedKey = signal("");
  const currentStart = signal("");
  const currentEnd = signal("");
  const currentOperatingProfit = signal("");
  const closing = signal(false);

  const currentYear = computed(
    () =>
      years.value.find(
        (y) => y.start_date === currentStart.value && y.end_date === currentEnd.value,
      ) ?? null,
  );
  const currentIsClosed = computed(() => currentYear.value?.status === "closed");
  const currentIsOpen = computed(() => !currentIsClosed.value);
  const currentRangeLabel = computed(() =>
    currentStart.value && currentEnd.value
      ? `${formatDate(currentStart.value)} – ${formatDate(currentEnd.value)}`
      : "",
  );

  const selectedAvailable = computed(
    () => available.value.find((y) => yearKey(y) === selectedKey.value) ?? null,
  );
  const showPicker = computed(() => available.value.length > 1);

  function reloadYears(): void {
    listAccountingYears()
      .then((result) => {
        years.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function reloadAvailable(): void {
    listAvailableAccountingYears()
      .then((result) => {
        available.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function reloadCurrentPeriod(): void {
    getPlReport({ comparison: false })
      .then((report) => {
        currentStart.value = report.start_date;
        currentEnd.value = report.end_date;
        currentOperatingProfit.value = report.operating_profit;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  reloadYears();
  reloadAvailable();
  reloadCurrentPeriod();

  async function doClose(startDate: string, endDate: string): Promise<void> {
    if (
      !window.confirm(
        `Close the accounting year ${startDate} to ${endDate}? This snapshots the P/L ` +
          "report as it stands right now — you can reopen it later if you need to.",
      )
    ) {
      return;
    }
    closing.value = true;
    try {
      await closeAccountingYear(startDate, endDate);
      toast.success("Accounting year closed.");
      reloadYears();
      reloadAvailable();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to close year");
    } finally {
      closing.value = false;
    }
  }

  async function doReopen(id: string, startDate: string, endDate: string): Promise<void> {
    if (
      !window.confirm(
        `Reopen the accounting year ${startDate} to ${endDate}? This clears ` +
          "its snapshot — later edits to that year will no longer be flagged until it's closed again.",
      )
    ) {
      return;
    }
    try {
      await reopenAccountingYear(id);
      toast.success("Accounting year reopened.");
      reloadYears();
      reloadAvailable();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to reopen year");
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card current-year-card">
      <div class="card-label">Current accounting year</div>
      <p class="current-year-range"></p>
      <p>Live operating profit: <strong class="current-year-profit"></strong></p>
      <div class="current-year-closed-note">
        <p>
          Closed on <span class="current-year-closed-at"></span> — snapshot operating profit:
          <strong class="current-year-snapshot-profit"></strong>
        </p>
        <button class="btn reopen-current-btn" type="button">Reopen this year</button>
      </div>
      <div class="current-year-open-actions">
        <button class="btn btn-primary close-current-btn" type="button">Close this year</button>
      </div>
    </div>

    <div class="card year-picker-card">
      <div class="card-label">Close a different year</div>
      <p>
        Every year back to your earliest bank transaction or issued invoice. Closing a past year
        snapshots the P/L as the books stand for it now — useful when entering prior-year accounts.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="year-picker">Accounting year</label>
          <select id="year-picker" class="year-picker"></select>
        </div>
        <div class="form-actions">
          <button class="btn btn-primary picker-action-btn" type="button"></button>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-label">Accounting years</div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th>Closed on</th>
              <th>Operating profit (at close)</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="years-body"></tbody>
        </table>
      </div>
      <p class="years-empty">No accounting year has been closed yet.</p>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const table = fragment.querySelector<HTMLTableElement>(".data-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".years-body");
  const emptyEl = fragment.querySelector<HTMLParagraphElement>(".years-empty");
  const rangeEl = fragment.querySelector<HTMLElement>(".current-year-range");
  const profitEl = fragment.querySelector<HTMLElement>(".current-year-profit");
  const closedNoteEl = fragment.querySelector<HTMLElement>(".current-year-closed-note");
  const closedAtEl = fragment.querySelector<HTMLElement>(".current-year-closed-at");
  const snapshotProfitEl = fragment.querySelector<HTMLElement>(".current-year-snapshot-profit");
  const reopenCurrentBtn = fragment.querySelector<HTMLButtonElement>(".reopen-current-btn");
  const openActionsEl = fragment.querySelector<HTMLElement>(".current-year-open-actions");
  const closeCurrentBtn = fragment.querySelector<HTMLButtonElement>(".close-current-btn");
  const pickerCard = fragment.querySelector<HTMLElement>(".year-picker-card");
  const picker = fragment.querySelector<HTMLSelectElement>(".year-picker");
  const pickerActionBtn = fragment.querySelector<HTMLButtonElement>(".picker-action-btn");

  if (
    !errorEl ||
    !table ||
    !tbody ||
    !emptyEl ||
    !rangeEl ||
    !profitEl ||
    !closedNoteEl ||
    !closedAtEl ||
    !snapshotProfitEl ||
    !reopenCurrentBtn ||
    !openActionsEl ||
    !closeCurrentBtn ||
    !pickerCard ||
    !picker ||
    !pickerActionBtn
  ) {
    throw new Error("year-end view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);
  bindText(rangeEl, currentRangeLabel);
  currentScope().effect(() => {
    profitEl.textContent = currentOperatingProfit.value
      ? formatMoney(currentOperatingProfit.value)
      : "—";
  });
  bindShow(closedNoteEl, currentIsClosed);
  bindShow(openActionsEl, currentIsOpen);
  currentScope().effect(() => {
    const year = currentYear.value;
    closedAtEl.textContent = year?.closed_at ? formatDate(year.closed_at) : "";
    snapshotProfitEl.textContent = year?.snapshot ? formatMoney(year.snapshot.operating_profit) : "";
  });
  currentScope().effect(() => {
    closeCurrentBtn.disabled = closing.value;
  });

  closeCurrentBtn.addEventListener("click", () => {
    void doClose(currentStart.value, currentEnd.value);
  });
  reopenCurrentBtn.addEventListener("click", () => {
    const year = currentYear.value;
    if (year) void doReopen(year.id, year.start_date, year.end_date);
  });

  bindShow(pickerCard, showPicker);

  // Rebuilt wholesale on every reload; the previous selection is restored by key so a
  // close/reopen leaves the user looking at the year they just acted on.
  currentScope().effect(() => {
    const options = available.value;
    const previous = selectedKey.value;
    picker.replaceChildren();
    for (const year of options) {
      const option = document.createElement("option");
      option.value = yearKey(year);
      option.textContent = availableLabel(year);
      picker.appendChild(option);
    }
    if (options.some((year) => yearKey(year) === previous)) {
      picker.value = previous;
    }
    selectedKey.value = picker.value;
  });

  picker.addEventListener("change", () => {
    selectedKey.value = picker.value;
  });

  currentScope().effect(() => {
    const year = selectedAvailable.value;
    pickerActionBtn.textContent = year?.status === "closed" ? "Reopen this year" : "Close this year";
    pickerActionBtn.disabled = year === null || closing.value;
  });

  pickerActionBtn.addEventListener("click", () => {
    const year = selectedAvailable.value;
    if (!year) return;
    if (year.status === "closed" && year.id) {
      void doReopen(year.id, year.start_date, year.end_date);
    } else {
      void doClose(year.start_date, year.end_date);
    }
  });

  currentScope().effect(() => {
    emptyEl.style.display = years.value.length === 0 ? "" : "none";
  });

  list(
    tbody,
    years,
    (year) => year.id,
    (year) => {
      const row = html`<tr>
        <td>${formatDate(year.start_date)}</td>
        <td>${formatDate(year.end_date)}</td>
        <td>
          <span class="badge ${STATUS_BADGE_CLASS[year.status]}">${STATUS_LABELS[year.status]}</span>
        </td>
        <td>${year.closed_at ? formatDate(year.closed_at) : "—"}</td>
        <td>${year.snapshot ? formatMoney(year.snapshot.operating_profit) : "—"}</td>
        <td>
          ${year.status === "closed"
            ? html`<button class="btn row-reopen-btn" type="button">Reopen</button>`
            : html`<button class="btn row-close-btn" type="button">Close</button>`}
        </td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("accounting year row template produced no element");
      const actionBtn = row.querySelector<HTMLButtonElement>(".row-reopen-btn, .row-close-btn");
      actionBtn?.addEventListener("click", () => {
        if (year.status === "closed") {
          void doReopen(year.id, year.start_date, year.end_date);
        } else {
          void doClose(year.start_date, year.end_date);
        }
      });
      return labelRow(row, table);
    },
  );

  return fragment;
}
